import { ConfiguracaoControleGabinete } from '../modelo/ConfiguracaoControleGabinete';
import { MenuItem } from '@mui/material';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import React from 'react';
import { TipoControle } from '../modelo';
import { ControleGabinete } from '../modelo/ControleGabinete';
import EscaninhoStore from './EscaninhoStore';
import UsuarioStore from './UsuarioStore';

export type ModoVisualizacaoControleGabinete = 'BLOCO' | 'LISTA';

class ControleGabineteStore {
    listaDeControles: ControleGabinete[] = [];
    indexBeingDragged: number;
    controlesEmEdicao: ControleGabinete[] = [];
    controlesEmEdicaoExcluidos: ControleGabinete[] = [];
    corHeaderControleGabinete: string = '#1976d2';
    controleParaEdicao: ControleGabinete;
    mostrarAddEditControleGabinete = false;
    carregandoControles = true;
    controlesCarregados = false;
    filtroOJC: string | string[] | null = null;
    visibilidadeAtiva = true;
    modoVisualizacao: ModoVisualizacaoControleGabinete = 'BLOCO';
    termoBusca: string = '';
    termoBuscaDebounced: string = '';
    buscando = false;
    servidoresPorControle = new Map<number, string[]>();

    // Enquanto o usuário interage com o Kanban (mouse se movendo, clicando ou rolando sobre ele) as
    // atualizações vindas do envelope ficam retidas, para a tela não mudar debaixo do cursor. Os
    // contadores abaixo alimentam o aviso "N atualizações pendentes".
    atualizacoesPausadas = false;
    processosComAtualizacaoPendente = 0;
    controlesComAtualizacaoPendente = false;

    private readonly ociosidadeParaRetomarMs = 4000;
    private readonly intervaloMinimoEntreInteracoesMs = 250;
    private ultimaInteracaoMs = 0;
    private timerOciosidade?: ReturnType<typeof setTimeout>;
    private debounceTimer?: ReturnType<typeof setTimeout>;

    constructor() {
        makeObservable(this, {
            listaDeControles: observable,
            indexBeingDragged: observable,
            controlesEmEdicao: observable,
            controlesEmEdicaoExcluidos: observable,
            controleParaEdicao: observable,
            mostrarAddEditControleGabinete: observable,
            carregandoControles: observable,
            controlesCarregados: observable,
            filtroOJC: observable,
            visibilidadeAtiva: observable,
            modoVisualizacao: observable,
            termoBusca: observable,
            termoBuscaDebounced: observable,
            buscando: observable,
            servidoresPorControle: observable,
            atualizacoesPausadas: observable,
            processosComAtualizacaoPendente: observable,
            controlesComAtualizacaoPendente: observable,
            corHeaderControleGabinete: observable,
            quantidadeAtualizacoesPendentes: computed,
            registrarInteracao: action,
            retomarAtualizacoes: action,
            setProcessosComAtualizacaoPendente: action,
            setControlesComAtualizacaoPendente: action,
            setFiltroOJC: action,
            limparFiltroOJC: action,
            setVisibilidadeAtiva: action,
            setModoVisualizacao: action,
            carregarModoVisualizacaoSalvo: action,
            setTermoBusca: action,
            limparTermoBusca: action,
            existeResultadoBusca: computed,
            setServidoresDoControle: action,
            isVisualizacaoEmBloco: computed,
            isVisualizacaoEmLista: computed,
            objetoSave: computed,
            trocarComIndiceMenor: action,
            trocarComIndiceMaior: action,
            carregarKanban: action,
            editarControle: action,
            atualizarDescricaoControle: action,
            atualizarCorControle: action,
            deletarControle: action,
            limpar: action
        });
    }

    setFiltroOJC(valor: string | string[] | null) {
        this.filtroOJC = valor;
    }

    limparFiltroOJC() {
        this.filtroOJC = null;
        this.visibilidadeAtiva = true;
    }

    setVisibilidadeAtiva(valor: boolean) {
        this.visibilidadeAtiva = valor;
    }

    setModoVisualizacao = (modo: ModoVisualizacaoControleGabinete) => {
        this.modoVisualizacao = modo;

        try {
            localStorage.setItem(this.storageKeyVisualizacao, modo);
        } catch {
            // Falha ao persistir (ex: storage indisponível). Ignorar e seguir.
        }
    };

    carregarModoVisualizacaoSalvo = () => {
        try {
            const salvo = localStorage.getItem(this.storageKeyVisualizacao);

            if (salvo === 'LISTA' || salvo === 'BLOCO') {
                this.modoVisualizacao = salvo;
            }
        } catch {
            // Falha ao ler storage -> mantém valor padrão (BLOCO)
        }
    };

    setTermoBusca = (valor: string) => {
        this.termoBusca = valor;
        this.buscando = true;

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        this.debounceTimer = setTimeout(() => {
            runInAction(() => {
                this.termoBuscaDebounced = valor;
                this.buscando = false;
            });
        }, 300);
    };

    limparTermoBusca = () => {
        this.termoBusca = '';
        this.termoBuscaDebounced = '';
        this.buscando = false;

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    };

    get existeResultadoBusca() {
        const termo = this.termoBuscaDebounced?.trim().toLowerCase();

        const controlesAtivos = this.listaDeControles.filter(c => c.ativo);

        return controlesAtivos.some(card => {
            if (!termo) return true;

            if (card.descricao?.toLowerCase().includes(termo)) {
                return true;
            }

            const servidores = this.servidoresPorControle.get(card.numero);

            if (!servidores || servidores.length === 0) {
                return false;
            }

            return servidores.some(nome =>
                nome.includes(termo)
            );
        });
    }

    setServidoresDoControle(controleNumero: number, servidores: string[]) {
        const novos = servidores.map(s => s.toLowerCase());
        const atuais = this.servidoresPorControle.get(controleNumero);

        // Evita notificar quem observa a busca quando nada mudou.
        if (atuais && atuais.length === novos.length && atuais.every((nome, i) => nome === novos[i])) {
            return;
        }

        this.servidoresPorControle.set(controleNumero, novos);
    }

    get quantidadeAtualizacoesPendentes() {
        return this.processosComAtualizacaoPendente + (this.controlesComAtualizacaoPendente ? 1 : 0);
    }

    /**
     * Marca que o usuário está interagindo com o Kanban e retém as atualizações. Volta ao normal
     * sozinho depois de um período sem interação, ou quando o mouse sai da área (retomarAtualizacoes).
     */
    registrarInteracao = () => {
        const agora = Date.now();

        // mousemove dispara dezenas de vezes por segundo; o timer de ociosidade é longo, então basta renová-lo de vez em quando.
        if (this.atualizacoesPausadas && agora - this.ultimaInteracaoMs < this.intervaloMinimoEntreInteracoesMs) {
            return;
        }

        this.ultimaInteracaoMs = agora;
        this.atualizacoesPausadas = true;

        if (this.timerOciosidade) {
            clearTimeout(this.timerOciosidade);
        }
        this.timerOciosidade = setTimeout(this.retomarAtualizacoes, this.ociosidadeParaRetomarMs);
    };

    retomarAtualizacoes = () => {
        if (this.timerOciosidade) {
            clearTimeout(this.timerOciosidade);
            this.timerOciosidade = undefined;
        }
        this.atualizacoesPausadas = false;
    };

    setProcessosComAtualizacaoPendente(quantidade: number) {
        this.processosComAtualizacaoPendente = quantidade;
    }

    setControlesComAtualizacaoPendente(pendente: boolean) {
        this.controlesComAtualizacaoPendente = pendente;
    }

    get isVisualizacaoEmBloco() {
        return this.modoVisualizacao === 'BLOCO';
    }

    get isVisualizacaoEmLista() {
        return this.modoVisualizacao === 'LISTA';
    }

    private get storageKeyVisualizacao() {
        return `controle-gabinete:visualizacao:${UsuarioStore.login}`;
    }

    get objetoSave() {
        return {
            nomeStore: 'ControleGabineteStore',
            listaDeControles: this.listaDeControles
        };
    }
    get controleDistribuir() {
        return this.listaDeControles.find(controle => controle.tipo.id === TipoControle.DISTRIBUIR);
    }

    get controleRedator() {
        return this.listaDeControles.find(controle => controle.tipo.id === TipoControle.REDATORDESIGNADO);
    }
    get controleVotoVencido() {
        return this.listaDeControles.find(controle => controle.tipo.id === TipoControle.VOTOVENCIDO);
    }

    get controlesGabMenuItem() {
        const retorno = this.listaDeControles
            .filter((card) => card.descricao !== 'Importar do PJe')
            .filter((card) => card.descricao !== 'Importar processos')
            .filter((card) => card.descricao !== 'Visto à pauta')
            .filter((card) => card.descricao !== 'Aguardando julgamento')
            .filter((card) => card.descricao !== 'Aguardando Julgamento')
            .filter((card) => card.descricao !== 'Para assinatura')
            .map((card, index) => (
                <MenuItem value={card.numero} key={index}>
                    {card.descricao}
                </MenuItem>
            ));
        /* return [...this.cardSelecione, ...retorno]; */

        if (EscaninhoStore.processosSelecionados.length > 1) {
            return [...this.cardSelecioneLote, ...retorno];
        } else {
            return [...retorno];
        }
    }

    readonly cardSelecioneLote = [
        <MenuItem value={-2} key={'menu-item-proxima-movimentacao-lote'}>
            Manter movimentação atual do processo
        </MenuItem>,
    ];

    buscarPorNumero(numero: number): ControleGabinete {
        const numInt = numero;
        return this.listaDeControles.find(controle => controle.numero === numInt);
    }

    buscarPorDescricao(descricao: string): ControleGabinete {
        return this.listaDeControles.find(controle => controle.descricao === descricao);
    }

    buscarPorTipo(tipo: TipoControle): ControleGabinete {
        return this.listaDeControles.find(controle => controle.isTipo(tipo));
    }



    trocarComIndiceMenor = (index: number) => {
        const indicePossivel = this.procuraIndicePossivelAEsquerda(index);
        if (indicePossivel && indicePossivel > 1) {
            const novaLista = this.controlesEmEdicao.slice();
            const elemento = novaLista.splice(index, 1);
            const indiceInsercao = index - indicePossivel > 1 ? indicePossivel + 1 : indicePossivel;
            novaLista.splice(indiceInsercao, 0, elemento[0]);
            this.controlesEmEdicao = novaLista;
        }
    };

    trocarComIndiceMaior = (index: number) => {
        const indicePossivel = this.procuraIndicePossivelADireita(index);
        if (indicePossivel && indicePossivel > 1) {
            const novaLista = this.controlesEmEdicao.slice();
            const elemento = novaLista.splice(index, 1);
            const indiceInsercao = indicePossivel - index > 1 ? indicePossivel - 1 : indicePossivel;
            novaLista.splice(indiceInsercao, 0, elemento[0]);
            this.controlesEmEdicao = novaLista;
        }
    };

    procuraIndicePossivelAEsquerda = (index: number) => {
        let i = index - 1;
        while (i >= 0) {
            const controle = this.controlesEmEdicao[i];
            if (controle
                && controle.tipo.id !== TipoControle.IMPORTAR
                && controle.tipo.id !== TipoControle.ASSINATURA
                && controle.tipo.id !== TipoControle.AGUARDANDO) {
                return i;
            } else {
                i--;
            }
        }
        return -1;
    }

    procuraIndicePossivelADireita = (index: number) => {
        let i = index + 1;
        while (i <= this.controlesEmEdicao.length) {
            const controle = this.controlesEmEdicao[i];
            if (controle
                && controle.tipo.id !== TipoControle.IMPORTAR
                && controle.tipo.id !== TipoControle.ASSINATURA
                && controle.tipo.id !== TipoControle.VISTOAPAUTA) {
                return i;
            } else {
                i++;
            }
        }
        return -1;
    }

    carregarKanban = () => {
        const cards = this.listaDeControles.slice();
        cards.forEach(card => {
            runInAction(() => {
                card.index = card.numeroOrdem;
            });
        });
        this.controlesEmEdicao = cards;
        this.controlesEmEdicaoExcluidos = [];
    };

    editarControle = (index) => {
        this.controleParaEdicao = this.controlesEmEdicao[index];
        this.controlesEmEdicao[index].index = index;
        this.mostrarAddEditControleGabinete = true;
    };

    /**
     * Atualiza a descrição de um controle já persistido em todas as listas locais que o
     * referenciam (listaDeControles, controlesEmEdicao e controleParaEdicao), substituindo
     * o item por uma nova instância — os campos de ControleGabinete não são observáveis
     * individualmente, então mutar em vez de substituir não notificaria o MobX.
     */
    atualizarDescricaoControle = (numero: number, novaDescricao: string) => {
        const substituir = (lista: ControleGabinete[]) => {
            const index = lista.findIndex(controle => controle.numero === numero);
            if (index === -1) {
                return null;
            }
            const atualizado = ControleGabinete.criar({ ...lista[index], descricao: novaDescricao });
            lista.splice(index, 1, atualizado);
            return atualizado;
        };

        substituir(this.listaDeControles);
        const atualizadoEmEdicao = substituir(this.controlesEmEdicao);

        if (this.controleParaEdicao?.numero === numero && atualizadoEmEdicao) {
            this.controleParaEdicao = atualizadoEmEdicao;
        }
    };

    atualizarCorControle = (numero: number, novaCor: string) => {
        const substituir = (lista: ControleGabinete[]) => {
            const index = lista.findIndex(controle => controle.numero === numero);
            if (index === -1) {
                return null;
            }
            const atualizado = ControleGabinete.criar({
                ...lista[index],
                configControle: ConfiguracaoControleGabinete.criar({
                    ...lista[index].configControle,
                    cor: novaCor
                })
            });
            lista.splice(index, 1, atualizado);
            return atualizado;
        };

        const atualizadoEmEdicao = substituir(this.controlesEmEdicao);

        if (this.controleParaEdicao?.numero === numero && atualizadoEmEdicao) {
            this.controleParaEdicao = atualizadoEmEdicao;
        }
    };

    deletarControle = (index) => {
        const cp = JSON.parse(JSON.stringify(this.controlesEmEdicao.splice(index, 1)));
        cp.ativo = false;
        this.controlesEmEdicaoExcluidos.push(cp[0]);
    };

    limpar = () => {
        this.listaDeControles = [];
        this.indexBeingDragged = undefined;
        this.controlesEmEdicao = [];
        this.controlesEmEdicaoExcluidos = [];
        this.controleParaEdicao = undefined;
        this.mostrarAddEditControleGabinete = false;
        this.carregandoControles = true;
        this.controlesCarregados = false;
        this.retomarAtualizacoes();
        this.processosComAtualizacaoPendente = 0;
        this.controlesComAtualizacaoPendente = false;
    };
}

export default new ControleGabineteStore();